import React, { FC } from "react";
import { TeamMembershipIds, Rate, FeeEarner } from "../../data-types";
import { TeamMembershipPartsFragment } from "~/gql/graphql";
import { Select } from "antd";
import { useNavigate } from "react-router";

const { Option } = Select;

type TeamMemberPickerProps = {
  teamMembers: TeamMembershipPartsFragment[];
  includedTeamMemberships: TeamMembershipIds[];
  selectedRate: Rate | null;
  selectedFeeEarner: FeeEarner | null;
  currencySymbol: string;
  onChange: (values: {
    feeEarnerId: number | null;
    selectedRateId: number | undefined;
    feeEarnerLevelId: number | null;
  }) => void;
  matterId: number;
};

export const TeamMemberPicker: FC<TeamMemberPickerProps> = ({
  teamMembers,
  includedTeamMemberships,
  selectedRate,
  selectedFeeEarner,
  currencySymbol,
  onChange,
  matterId,
}) => {
  const selectedTeamMember = teamMembers.find(
    (x) =>
      x.rateId === selectedRate?.id &&
      (x.feeEarnerId === selectedFeeEarner?.id ||
        (x.feeEarnerId === null && selectedFeeEarner === null))
  );

  const includedTeamMembers = teamMembers.filter((teamMember) => {
    return includedTeamMemberships.some(
      (includedTeamMembership) =>
        includedTeamMembership.rateId === teamMember.rateId &&
        includedTeamMembership.feeEarnerId === teamMember.feeEarnerId
    );
  });

  const navigate = useNavigate();

  const teamMemberToLabel = (teamMember: TeamMembershipPartsFragment | undefined) => {
    if (!teamMember) {
      return "";
    }
    if (teamMember.feeEarner) {
      return `${teamMember.feeEarner.feeEarnerLevel?.description} - ${teamMember.feeEarner.name} | ${currencySymbol}${teamMember.rate.amount}`;
    }
    return `${teamMember.rate.description} | ${currencySymbol}${teamMember.rate.amount}`;
  };

  const triggerNavigate = () => {
    navigate(`/matter-editor/${matterId}/team`);
  };

  return (
    <Select
      placeholder="Select a team member"
      size="small"
      style={{ width: "350px" }}
      labelInValue
      value={
        selectedRate && selectedTeamMember
          ? {
              key: [selectedRate.id, selectedFeeEarner?.id].join("-" as string) as string,
              label: teamMemberToLabel(selectedTeamMember),
            }
          : null
      }
      onChange={({ key }) => {
        if (key === "add") {
          triggerNavigate();
          return;
        }
        const parsedKey = parseInt(key as unknown as string, 10);
        const selectedTeamMember = teamMembers.find((x) => x.id === parsedKey);
        onChange({
          feeEarnerId: selectedTeamMember?.feeEarner?.id || null,
          selectedRateId: selectedTeamMember?.rateId,
          feeEarnerLevelId:
            selectedTeamMember?.feeEarner?.feeEarnerLevel?.id ||
            selectedTeamMember?.rate?.feeEarnerLevel?.id ||
            null,
        });
      }}
    >
      {teamMembers
        .filter((teamMember) => !includedTeamMembers.includes(teamMember))
        .map((teamMember) => {
          const label = teamMemberToLabel(teamMember);
          return (
            <Option key={teamMember.id} value={teamMember.id} label={label}>
              {label}
            </Option>
          );
        })}
      <Option key={"add"} label={<em>Add to Team</em>}>
        <em>Add to Team</em>
      </Option>
    </Select>
  );
};
